import {Subject, BehaviorSubject, combineLatest} from 'rxjs';
import {skipUntil, switchMap, map} from 'rxjs/operators';
import {mc} from '@/core';
import {ORDER_TYPE, ITEM_TRADE_TYPE} from '@/modules/assets/map';
import {gt, numUtil} from '@/helper/num';
import {timeUtil} from '@/helper/time';
import {calcCurrentQty} from '@/helper/contract';
import {assetsApi} from '@/api/assets';
import {browser$} from '@/streams/browser';
import {contracts$} from '@/streams/config/contract';
import {currencies$} from '@/streams/config/currency';
import {qtyTypeBeforeV2$, qtyTypeV2$} from '@/streams/contract';
import {formatterUpgradedBeforeAfter} from '@/modules/assets/fund-details/util';
import {HistoryQueryUtaState} from '@/modules/assets/const';

export const loading$ = new Subject();
export const query$ = new Subject();

const tradeOrders$ = new BehaviorSubject({total: 0, rows: []});

export const tradeList$ = combineLatest([tradeOrders$, contracts$, currencies$, qtyTypeBeforeV2$, qtyTypeV2$]).pipe(map(convertData));

query$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadTradeList(query))
  )
  .subscribe();

async function loadTradeList({currency, offset, symbol, execType, isPerpetualV2, utaState = HistoryQueryUtaState.beforeUpgrade}) {
  loading$.next(true);
  const query = {
    currency,
    symbol,
    offset,
    execType,
    limit: 10,
    withCount: true,
  };

  // 升级前后记录区分
  const query2 = formatterUpgradedBeforeAfter(
    utaState,
    query,
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      end: unifiedAccountUpgradedTime,
    }),
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      start: unifiedAccountUpgradedTime,
    })
  );
  const {data, error} = isPerpetualV2 ? await assetsApi.tradeListV2(query2) : await assetsApi.tradeList(query2);

  loading$.next(false);
  if (data) {
    tradeOrders$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

function convertData([orders, contracts, currencies, qtyTypeBeforeV2, qtyTypeV2]) {
  const {rows, total} = orders;
  let list = convertTradeHistory({rows, contracts, currencies, qtyTypeBeforeV2, qtyTypeV2});
  list = list.filter(Boolean);
  return {rows: list, total};
}

export function convertTradeHistory({rows, contracts, currencies, qtyTypeBeforeV2, qtyTypeV2}) {
  qtyTypeBeforeV2 = qtyTypeBeforeV2 || qtyTypeBeforeV2$.getValue();
  qtyTypeV2 = qtyTypeV2 || qtyTypeV2$.getValue();

  return rows.map((item) => {
    const contract = contracts.find((contract) => contract.symbol === item.symbol);
    if (!contract) {
      return null;
    }
    const {isPerpetualV2, pricePrecision, priceScale, settleCurrencyDisplay: settleCurrency, symbolV2} = contract;
    const currency = currencies.find((o) => o.currency === contract.settleCurrency);
    if (!currency) {
      return null;
    }
    const {valueScale, valuePrecision} = currency;
    const data = {
      createdTime: timeUtil.formatUTCDateTime(item.createdAt),
      symbol: item.symbol,
      displaySymbol: `${symbolV2} ${settleCurrency}-Margin`,
      type: genType(item),
      execType: item.tradeType === 1 ? 'Trade' : item.tradeType === 6 ? 'Liquidation' : 'ADL',
      execId: item.execId,
      side: item.side,
      tradeType: item.tradeType,
    };
    if (isPerpetualV2) {
      item.priceEp = Number(item.priceRp);
      item.execPriceEp = Number(item.execPriceRp);
      item.execValueEv = Number(item.execValueRv);
      item.execFeeEv = Number(item.execFeeRv);
      const qtyPrice = Number(item.execPriceRp) !== 0 ? item.execPriceRp : item.priceRp;
      Object.assign(data, {
        execQty: calcCurrentQty(contract, item.execQtyRq, qtyPrice, qtyTypeBeforeV2, qtyTypeV2),
        execPrice: genExecPrice(item, pricePrecision, 0),
        orderQty: calcCurrentQty(contract, item.orderQtyRq, qtyPrice, qtyTypeBeforeV2, qtyTypeV2),
        orderPrice: genOrderPrice(item, pricePrecision, 0),
        execValue: genExecValve(item, settleCurrency, 0, valuePrecision),
        feeRate: numUtil.floor(item.feeRateRr, 4, -2) + '%',
        feePaid: genFeePaid(item, settleCurrency, 0, valuePrecision),
        posSide: item.posSide,
        execPriceEp: item.execPriceEp,
        priceEp: item.priceEp,
      });
      return data;
    }
    const qtyPrice = genQtyPrice(item, pricePrecision, priceScale);
    Object.assign(data, {
      execQty: (item.side === 1 ? '' : '-') + calcCurrentQty(contract, item.execQty, qtyPrice, qtyTypeBeforeV2, qtyTypeV2),
      execPrice: genExecPrice(item, pricePrecision, priceScale),
      orderQty: (item.side === 1 ? '' : '-') + calcCurrentQty(contract, item.orderQty, qtyPrice, qtyTypeBeforeV2, qtyTypeV2),
      orderPrice: genOrderPrice(item, pricePrecision, priceScale),
      execValue: genExecValve(item, settleCurrency, valueScale, valuePrecision),
      feeRate: numUtil.floor(item.feeRateEr, 4, 6) + '%',
      feePaid: genFeePaid(item, settleCurrency, valueScale, valuePrecision),
      execPriceEp: item.execPriceEp,
      priceEp: item.priceEp,
    });
    return data;
  });
}

function genType(item) {
  const {ordType, tradeType} = item;
  if (ordType === 0) {
    return ITEM_TRADE_TYPE[tradeType];
  }
  return ORDER_TYPE[ordType] || '--';
}

function genQtyPrice(item, pricePrecision, priceScale) {
  const {execPriceEp, priceEp} = item;
  const price = Number(execPriceEp) !== 0 ? execPriceEp : priceEp;
  return numUtil.floor(price, pricePrecision, priceScale);
}

function genExecPrice(item, pricePrecision, priceScale) {
  const {execPriceEp, priceEp, tradeType} = item;
  return numUtil.floor(tradeType === 6 ? priceEp : execPriceEp, pricePrecision, priceScale);
}

function genExecValve(item, settleCurrency, valueScale, valuePrecision) {
  return numUtil.floor(item.execValueEv, valuePrecision, valueScale) + ' ' + settleCurrency;
}

function genOrderPrice(row, pricePrecision, priceScale) {
  const {ordType, priceEp} = row;
  if ([1, 3, 5, 14].indexOf(ordType) > -1) {
    return 'Market';
  }
  if (priceEp === 0) {
    return '--';
  }
  return numUtil.floor(priceEp, pricePrecision, priceScale);
}

function genFeePaid(item, settleCurrency, valueScale, valuePrecision) {
  if (item.ptFeeRv && gt(item.ptFeeRv, 0)) {
    return numUtil.floor(item.ptFeeRv, valuePrecision, 0) + ' PT';
  }

  return numUtil.floor(item.execFeeEv, valuePrecision, valueScale) + ' ' + settleCurrency;
}
